import {
  Component,
  OnDestroy,
  OnInit,
  TemplateRef,
  ViewChild,
} from "@angular/core";
import { Router } from "@angular/router";
import { UUID } from "angular2-uuid";
import { uniqBy } from "lodash";
import { LazyLoadEvent } from "primeng/api";
import { Observable, of, Subject } from "rxjs";
import { first, takeUntil } from "rxjs/operators";
import { TokenService } from "src/app/auth/token.service";
import { BuildingSurveyViewModel } from "src/app/shared/Models/buildingTableViewModel";
import { AddressTypeEnum } from "src/app/shared/Models/enums/AddressTypeEnum";
import { BuildingTypeEnum } from "src/app/shared/Models/enums/BuildingTypeEnum";
import { ColumnTypes } from "src/app/shared/Models/enums/ColumnTypeEnum";
import { PermissionsEnum } from "src/app/shared/Models/enums/PermissionsEnum";
import { SurveyStatus } from "src/app/shared/Models/enums/SurveySurveyStatus";
import { CreateInsideSurveyDTO } from "src/app/shared/Models/write/createInsideSurveyDTO";
import { CreateOutsideSurveyDto } from "src/app/shared/Models/write/CreateOutsideSurveyDto";
import {
  Figures,
  RandomFigureGeneratorService,
} from "src/app/shared/services/figure.service";
import { InsideSurveyService } from "src/app/shared/services/inside-survey.service";
import { PermissionsService } from "src/app/shared/services/permissions.service";
import { FilterableTableComponent } from "src/app/shared/table/filterable-table.component";
import { ToastService } from "src/app/shared/toast.service";
import { BuildingService } from "../../building-list/services/building.service";
import { DeviceCheckService } from "../../survey/shared/services/device-check.service";
import { AreaService } from "src/app/shared/services/area.service";
import { Area } from "src/app/shared/Models/read";
import { BuildingSearchService } from "../services/buildingsearchservice";

@Component({
  selector: "app-building-search",
  templateUrl: "./building-search.component.html",
  styleUrls: ["./building-search.component.scss"],
})
export class BuildingSearchComponent extends FilterableTableComponent<BuildingSurveyViewModel> {
  searchExecuted: boolean = false;
  searchValue: string = "";
  allSearchResults: BuildingSurveyViewModel[] = [];
  BuildingTypeEnum = BuildingTypeEnum;
  AddressTypeEnum = AddressTypeEnum;
  surveyStatus = SurveyStatus;
  scrollOffsetLandscape: number = 206;
  scrollOffsetPortrait: number = 196;

  loading: boolean = false;

  allCols: any[] = [
    {
      field: "buildingGroupId",
      header: $localize`:@@general.buildinggroup.short:BG`,
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "buildingGroup",
      transform: (data: BuildingSurveyViewModel) => data.figure,
      filterSearch: false,
      hasEmptyValue: true,
    },
    {
      field: "buildingLomKey",
      header: "BID",
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
      ignoredFilterValues: [0],
    },
    {
      field: "addressLomKey",
      header: "GAID",
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: true,
      ignoredFilterValues: [0],
    },
    {
      field: "street",
      header: $localize`:@@general.street:Street`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "number",
      header: "NR",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "suffix",
      header: $localize`:@@general.suffix.short:Sfx`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "location",
      header: $localize`:@@general.location:Location`.toUpperCase(),
      visibleOnPortrait: true,
      hasFilter: false,
    },
    {
      field: "outsideEngineer.firstName",
      header: $localize`:@@general.engineer:Engineer`.toUpperCase(),
      visibleOnPortrait: false,
      hasFilter: true,
      type: ColumnTypes.STRING,
      template: "default",
      filterSearch: true,
      hasEmptyValue: false,
    },
    {
      field: "buildingType",
      header: "TYPE",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "default",
      transform: (data: BuildingSurveyViewModel) =>
        BuildingTypeEnum[data.buildingType],
      filterSearch: true,
      hasEmptyValue: true,
    },
    {
      field: "isMDU",
      header: $localize`:@@general.mdu: MDU`,
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.BOOLEAN,
      template: "isMDU",
      filterSearch: false,
      hasEmptyValue: true,
      ignoredFilterValues: [false],
    },
    {
      field: "surveyStatus",
      header: "STATUS",
      visibleOnPortrait: true,
      hasFilter: true,
      type: ColumnTypes.NUMBER,
      template: "surveyStatus",
      filterSearch: false,
      hasEmptyValue: false,
    },
    {
      field: "hiddenData",
      header: "hiddenData",
      visibleOnPortrait: true,
      hasFilter: false,
      hidden: true,
    },
  ];

  @ViewChild("surveyStatus") surveyStatusTemplate: TemplateRef<{
    data: SurveyStatus;
  }>;
  @ViewChild("isMDU") isMDUTemplate: TemplateRef<{
    data: boolean;
  }>;
  @ViewChild("buildingGroup") buildingGroupTemplate: TemplateRef<{
    data: { Figure: Figures; Color: string };
  }>;

  constructor(
    private buildingSearchService: BuildingSearchService,
    private router: Router,
    private tokenService: TokenService,
    private insideSurveyService: InsideSurveyService,
    private buildingService: BuildingService,
    private figureService: RandomFigureGeneratorService,
    toastService: ToastService,
    deviceCheckService: DeviceCheckService,
    private permissionsService: PermissionsService,
    private areaService: AreaService
  ) {
    super(toastService, deviceCheckService);
  }

  getSearchResults(value: string, page: number): void {
    this.loading = true;
    this.searchExecuted = true;

    this.buildingSearchService
      .searchBuildings(page, value)
      .pipe(takeUntil(this.destroy$))
      .subscribe(
        (data) => {
          let results = data.items.map((i) => {
            return new BuildingSurveyViewModel().deserialize(i);
          });

          this.allSearchResults =
            this.buildingService.getSortedAddressTable(results);

          this.tableValues = uniqBy(this.allSearchResults, "buildingId");
          this.figureService.MapFigureToBuildingGroup(this.tableValues);

          this.loading = false;
        },
        () => {
          this.loading = false;
        }
      );
  }

  executeSearch(): void {
    if (this.searchValue == "") {
      this.toastService.showErrorToast(
        $localize`:@@validation.general.invalidinput:Invalid input!`,
        $localize`:@@validation.search.invalidinput:Input provided for search is not valid`
      );
      this.loading = false;
    } else {
      this.getSearchResults(this.searchValue, 1);
    }
  }

  searchIsEmpty(): boolean {
    return !this.searchValue.trim();
  }

  onStartSurvey(building: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (!this.permissionsService.allowed(PermissionsEnum.StartSurvey)) {
      var PH = this.tokenService.getRoles()[0];
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
        $localize`:@@validation.survey.viewercantstartsurvey:${PH}:INTERPOLATION: is not allowed to start a Survey.`
      );
      return;
    }

    if (
      (building.hasFailedValidateSurvey || building.hasOngoingValidateSurvey) &&
      !this.permissionsService.allowed(PermissionsEnum.ForceStartSurvey)
    ) {
      let state = building.hasFailedValidateSurvey ? "failed" : "ongoing";
      this.toastService.showErrorToast(
        $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
        $localize`:@@validation.survey.cantstartsurveywithongoingvalidation: Starting the survey is not allowed while a validation is ${state}.`
      );
      return;
    }

    const userId = this.tokenService.getAuthUserId();
    let func: Observable<any>;
    let surveyStatus;
    const surveyId = UUID.UUID();

    const createInside = () => {
      if (!building.allInGroupReadyToInside) func = of(false);
      surveyStatus = SurveyStatus.InsideOnGoing;
      func = this.insideSurveyService.createInsideSurvey(
        new CreateInsideSurveyDTO({
          buildingId: building.buildingId,
          insideEngineerId: userId,
          insideSurveyId: surveyId,
          buildingGroupId: building.buildingGroupId,
        })
      );
    };

    const createOutside = () => {
      surveyStatus = SurveyStatus.OutsideOnGoing;

      func = this.buildingService.createOutsideSurvey(
        new CreateOutsideSurveyDto({
          surveyId,
          buildingId: building.buildingId,
          outsideEngineerId: userId,
          isPrefill: false,
        })
      );
    };

    switch (building.surveyStatus) {
      case SurveyStatus.OutsideDone:
      case SurveyStatus.OutsideOnGoing:
      case SurveyStatus.OutsideWaitingForValidation:
      case SurveyStatus.OutsidePending:
        createOutside();
        break;
      case SurveyStatus.InsideDone:
      case SurveyStatus.InsideOnGoing:
      case SurveyStatus.InsidePending:
        createInside();
        break;
      default:
        break;
    }

    func.pipe(first()).subscribe(
      () => {
        this.router.navigate(["/survey", surveyStatus, surveyId, false]);
      },
      (error) => {
        if (error.error.code === "CantCreateInsideSurvey")
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.buildinggroupoutsidenotfinished:Building group has buildings with not finished outside surveys.`
          );
        else
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.startsomethingwentwrong:Something went wrong while trying to start the survey.`
          );

        this.router.navigateByUrl("/search");
      }
    );
  }

  onGoToSurvey(building: BuildingSurveyViewModel) {
    if (!navigator.onLine) {
      this.toastService.showWarnToast(
        $localize`:@@general.nointernet:No internet`,
        $localize`:@@general.nointernet.actionnotpossible:This action is not possible while you're offline.`
      );
      return;
    }

    if (!this.permissionsService.allowed(PermissionsEnum.ViewSurvey)) return;

    var surveyId;
    switch (building.surveyStatus) {
      case SurveyStatus.OutsideDone:
      case SurveyStatus.OutsideOnGoing:
      case SurveyStatus.OutsideWaitingForValidation:
      case SurveyStatus.OutsidePending:
        surveyId = building.surveyId;
        break;
      case SurveyStatus.InsideDone:
      case SurveyStatus.InsideOnGoing:
      case SurveyStatus.InsidePending:
      case SurveyStatus.InsideWaitingForValidation:
        if (!building.allInGroupReadyToInside) {
          this.toastService.showErrorToast(
            $localize`:@@validation.survey.cantstartsurvey:Can\'t start survey!`,
            $localize`:@@validation.survey.buildinggroupoutsidenotfinished:Building group has buildings with not finished outside surveys.`
          );
          this.router.navigateByUrl("/search");
          return;
        }
        surveyId = building.insideSurveyId;
        break;
      default:
        break;
    }
    this.router.navigate(["/survey", building.surveyStatus, surveyId, false]);
  }

  openBuildingList(building: BuildingSurveyViewModel) {
    sessionStorage.setItem("city", building.areaCity);
    sessionStorage.setItem("areaId", building.areaId);
    this.setFiberHood(building.areaCity, building.fiberHood, building.block);
  }

  setFiberHood(selectedCity, fiberhood, blockString) {
    this.areaService
      .getFiberHoodsForCityName(selectedCity)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: Area[]) => {
        const currentFiberhood = res.find((x) => x.fiberHood === fiberhood);
        sessionStorage.setItem("fiberhood", JSON.stringify(currentFiberhood));
        this.setBlock(currentFiberhood.id, blockString);
      });
  }

  setBlock(parentId: string, block: string) {
    this.areaService
      .getBlocksForFiberHoodId(parentId)
      .pipe(takeUntil(this.destroy$))
      .subscribe((res: Area[]) => {
        const currentBlock = res.find((x) => x.block.toString() === block);
        sessionStorage.setItem("block", JSON.stringify(currentBlock));
        this.router.navigate(["/building-table"]);
      });
  }
}
